import { useQuery } from "@tanstack/react-query";
import { useMemo, useRef } from "react";

import { useEnableGcpFakeSystemEngine } from "featureFlags/hooks/useEnableGcpFakeSystemEngine";
import { useLegacySearch } from "featureFlags/hooks/useLegacySearch";
import { useNewSidebarExperience } from "featureFlags/hooks/useNewSidebarExperience";
import { Database } from "services/databases/useDatabasesNames";
import { systemEngineEnvironment } from "services/environment/systemEngine";

import { useCurrentAccount } from "components/Account/useCurrentAccount";
import { ItemDataType } from "components/Tree/types";

import { SearchRow, buildSearchTree } from "./buildSearchTree";
import {
  DATABASE_LIMIT,
  buildSearchSql,
  chunkDatabases,
} from "./searchSqlBuilders";

type SearchResult = {
  data: ItemDataType[];
  searchTerm: string;
  isLoading: boolean;
  isLegacySearch: boolean;
  newSearchExperience: boolean;
};

/**
 * Hook to perform server-side database object search.
 * Falls back to client-side search when:
 * - legacySearch feature flag is enabled
 * - Object count exceeds limit
 * - Count query fails or is loading
 */
export const useSearchData = (
  databases: Database[] = [],
  searchTerm = ""
): SearchResult => {
  const { getAccount } = useCurrentAccount();
  const endpoint = useEnableGcpFakeSystemEngine();
  const newSidebarExperience = useNewSidebarExperience();
  const legacySearch = useLegacySearch();

  const accountName = getAccount().accountName;
  const normalizedSearchTerm = searchTerm.trim().toLowerCase();
  const hasSearchTerm = normalizedSearchTerm.length > 0;

  // Cache last successful search results for stability during loading
  const lastResultRef = useRef<{ data: ItemDataType[]; searchTerm: string }>({
    data: [],
    searchTerm: "",
  });

  const databaseKey = useMemo(
    () => databases.map(db => db.catalogName).join("|"),
    [databases]
  );

  const shouldUseLegacySearch =
    !newSidebarExperience || legacySearch || databases.length > DATABASE_LIMIT;

  // Query: Perform the actual search
  const searchQuery = useQuery({
    queryKey: [
      "database-search-tree",
      accountName,
      endpoint,
      databaseKey,
      normalizedSearchTerm,
    ],
    enabled:
      newSidebarExperience &&
      databases.length > 0 &&
      hasSearchTerm &&
      !shouldUseLegacySearch,
    staleTime: 0,
    retry: false,
    queryFn: async ({ signal }) => {
      const chunks = chunkDatabases(databases);
      const results = await Promise.all(
        chunks.map(chunk =>
          systemEngineEnvironment.execute<SearchRow>(buildSearchSql(chunk), {
            accountName,
            endpoint,
            signal,
            namedParameters: { searchTerm: `%${normalizedSearchTerm}%` },
          })
        )
      );

      const rows: SearchRow[] = [];
      for (const [result] of results) {
        const chunkRows = result?.rows || [];
        rows.push(...chunkRows);
      }

      return {
        status: "complete" as const,
        data: buildSearchTree(rows, databases),
      };
    },
  });

  // Update cache when search completes successfully
  const searchData = searchQuery.data;
  if (searchData?.status === "complete" && normalizedSearchTerm) {
    lastResultRef.current = {
      data: searchData.data,
      searchTerm: normalizedSearchTerm,
    };
  }

  // Clear cache when search term is cleared
  if (!normalizedSearchTerm && lastResultRef.current.searchTerm) {
    lastResultRef.current = { data: [], searchTerm: "" };
  }

  const isComplete = searchData?.status === "complete";
  const isFallback = shouldUseLegacySearch || searchQuery.error;

  const stableData = isComplete ? searchData.data : lastResultRef.current.data;
  const stableSearchTerm = isComplete
    ? normalizedSearchTerm
    : lastResultRef.current.searchTerm;

  return {
    data: searchQuery.error ? [] : stableData,
    searchTerm: stableSearchTerm,
    isLoading: searchQuery.isLoading && !isComplete,
    isLegacySearch: shouldUseLegacySearch,
    newSearchExperience: newSidebarExperience && hasSearchTerm && !isFallback,
  };
};
